import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import CategoryRestrictionQuery from '@/services/query/categoryRestriction/CategoryRestrictionQuery.services';
import { CategoryNode } from '../growCategoryRestriction.types';
import {
    collapseAllNodes,
    collectRestrictedIds,
    extractCategoryNodes,
    getApiErrorMessage,
    isExpandableNode,
    mergeChildLists,
    normalizeCategoryTree,
    updateNode,
} from '../growCategoryRestriction.helpers';

const applyLoadedChildrenUpdate =
    (normalizedApiChildren: CategoryNode[]) =>
    (n: CategoryNode): CategoryNode => ({
        ...n,
        isExpanded: true,
        isLoadingChildren: false,
        hasChildren: isExpandableNode(n),
        children: mergeChildLists(n.children ?? [], normalizedApiChildren),
    });

const applyLoadErrorUpdate = (n: CategoryNode): CategoryNode => ({
    ...n,
    isLoadingChildren: false,
    isExpanded: !!(n.children && n.children.length > 0),
});

const applyCollapseUpdate = (n: CategoryNode): CategoryNode => ({
    ...n,
    isExpanded: false,
    isLoadingChildren: false,
});

const applyExpandLoadingUpdate = (n: CategoryNode): CategoryNode => ({
    ...n,
    isExpanded: true,
    isLoadingChildren: true,
});

async function fetchCategoryChildren(countryId: number | '', node: CategoryNode) {
    const res = await CategoryRestrictionQuery.GetCategoryRestrictionChildren({
        CountryId: countryId,
        ParentId: node.categoryId,
    });
    return normalizeCategoryTree(extractCategoryNodes(res));
}

interface UseCategoryHierarchyTreeParams {
    countryId: number | '';
    overrideNodes?: CategoryNode[] | null;
    reloadKey?: number;
    isRestricting?: boolean;
    onDiscoverRestrictedIds?: (ids: Set<number>) => void;
}

export const useCategoryHierarchyTree = ({
    countryId,
    overrideNodes,
    reloadKey = 0,
    isRestricting = false,
    onDiscoverRestrictedIds,
}: UseCategoryHierarchyTreeParams) => {
    const { t } = useTranslation();
    const [rootNodes, setRootNodes] = useState<CategoryNode[]>([]);
    const [isLoadingRoot, setIsLoadingRoot] = useState(false);
    const [fetchingCount, setFetchingCount] = useState(0);

    // Keep latest busy flags in refs so expand handler stays stable.
    const fetchingCountRef = useRef(0);
    const isRestrictingRef = useRef(isRestricting);
    fetchingCountRef.current = fetchingCount;
    isRestrictingRef.current = isRestricting;

    const onDiscoverRef = useRef(onDiscoverRestrictedIds);
    onDiscoverRef.current = onDiscoverRestrictedIds;

    const showLoadError = useCallback(
        (err: any) => {
            Swal.fire({
                icon: 'error',
                title: t('growCategoryRestriction.hierarchy.loadError'),
                text: getApiErrorMessage(err, t('growCategoryRestriction.hierarchy.unknownError')),
                confirmButtonColor: '#1976d2',
            });
        },
        [t]
    );

    const notifyDiscovered = useCallback((nodes: CategoryNode[]) => {
        const discovered = collectRestrictedIds(nodes);
        if (discovered.size > 0) onDiscoverRef.current?.(discovered);
    }, []);

    useEffect(() => {
        if (overrideNodes != null) {
            // Parent already normalizes search results; re-normalize is cheap + idempotent.
            const normalized = normalizeCategoryTree(overrideNodes);
            setRootNodes(normalized);
            setFetchingCount(0);
            notifyDiscovered(normalized);
            return;
        }

        if (countryId === '') return;

        let cancelled = false;
        setIsLoadingRoot(true);
        setRootNodes([]);
        setFetchingCount(0);

        CategoryRestrictionQuery.GetCategoryRestrictionChildren({ CountryId: countryId })
            .then((res: any) => {
                if (cancelled) return;
                const nodes = normalizeCategoryTree(extractCategoryNodes(res));
                setRootNodes(nodes);
                notifyDiscovered(nodes);
            })
            .catch((err: any) => {
                if (!cancelled) showLoadError(err);
            })
            .finally(() => {
                if (!cancelled) setIsLoadingRoot(false);
            });

        return () => {
            cancelled = true;
        };
        // reloadKey intentionally triggers re-fetch
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [countryId, overrideNodes, reloadKey]);

    const collapseAll = useCallback(() => {
        setRootNodes((prev) => collapseAllNodes(prev));
        setFetchingCount(0);
    }, []);

    const toggleExpand = useCallback(
        (node: CategoryNode) => {
            if (!isExpandableNode(node) || fetchingCountRef.current > 0 || isRestrictingRef.current) return;

            if (node.isExpanded) {
                setRootNodes((prev) => updateNode(prev, node.categoryId, applyCollapseUpdate));
                return;
            }

            setFetchingCount((c) => c + 1);
            setRootNodes((prev) => updateNode(prev, node.categoryId, applyExpandLoadingUpdate));

            const loadChildren = async () => {
                try {
                    const normalizedApiChildren = await fetchCategoryChildren(countryId, node);
                    notifyDiscovered(normalizedApiChildren);
                    setRootNodes((prev) =>
                        updateNode(prev, node.categoryId, applyLoadedChildrenUpdate(normalizedApiChildren))
                    );
                } catch (err) {
                    setRootNodes((prev) => updateNode(prev, node.categoryId, applyLoadErrorUpdate));
                    showLoadError(err);
                } finally {
                    setFetchingCount((c) => Math.max(0, c - 1));
                }
            };

            loadChildren();
        },
        [countryId, notifyDiscovered, showLoadError]
    );

    return {
        rootNodes,
        isLoadingRoot,
        isBusy: isLoadingRoot || fetchingCount > 0 || isRestricting,
        collapseAll,
        toggleExpand,
    };
};
